/**
 * Retrato do pedido recém-fechado que a tela de sucesso (`/sucesso`) exibe.
 *
 * O `GlobalContext` guarda `orderData`/`cartTotals`/`orderItems` só em memória e o carrinho já foi
 * esvaziado no fechamento. Sem este retrato, recarregar a página (F5, pull-to-refresh, atualização
 * do app) abria "Pedido realizado!" com o pedido zerado. Fica em `sessionStorage` para sobreviver ao
 * reload sem vazar para outra aba nem para a próxima abertura do PWA.
 */
export const ORDER_SUCCESS_SNAPSHOT_SESSION_KEY = 'rca_order_success_snapshot';

export type OrderSuccessSnapshot = {
  orderData: any;
  cartTotals: any;
  orderItems: any[];
};

const isValidSnapshot = (value: any): value is OrderSuccessSnapshot =>
  !!value?.orderData?.orders && !!value?.cartTotals && Array.isArray(value?.orderItems);

export function clearOrderSuccessSnapshot(): void {
  try {
    sessionStorage.removeItem(ORDER_SUCCESS_SNAPSHOT_SESSION_KEY);
  } catch {
    /* ignore private mode */
  }
}

export function readOrderSuccessSnapshot(): OrderSuccessSnapshot | null {
  try {
    const raw = sessionStorage.getItem(ORDER_SUCCESS_SNAPSHOT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isValidSnapshot(parsed)) return parsed;
  } catch {
    /* JSON inválido ou storage indisponível */
  }
  clearOrderSuccessSnapshot();
  return null;
}

/** Retorna `false` quando não gravou; nesse caso não sobra retrato antigo de outro pedido. */
export function saveOrderSuccessSnapshot(snapshot: OrderSuccessSnapshot): boolean {
  if (!isValidSnapshot(snapshot)) return false;
  try {
    sessionStorage.setItem(ORDER_SUCCESS_SNAPSHOT_SESSION_KEY, JSON.stringify(snapshot));
    return true;
  } catch {
    clearOrderSuccessSnapshot();
    return false;
  }
}
